import { Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  FormsModule,
  Validators,
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ExecuteConfigurationDto } from '@api-types/dtos/execute-configuration.dto';
import { Resolution } from '@api-types/enums/resolution.enum';
import { PmDialogComponent } from '@libs/ui';
import { TranslateModule } from '@ngx-translate/core';
import { format } from 'date-fns';
import { map } from 'rxjs';
import { ForecastVersionSettingsComponent } from 'demand-forecasting/components/create-configuration/configuration-steps/forecast-version-settings/forecast-version-settings.component';
import { ForecastVersionSettingsForm } from 'demand-forecasting/components/forms/forecast-version-settings-form.interface';

@Component({
  selector: 'app-re-execute-with-limited-data-dialog',
  templateUrl: './re-execute-with-limited-data-dialog.component.html',
  imports: [
    ForecastVersionSettingsComponent,
    TranslateModule,
    FormsModule,
    PmDialogComponent,
  ],
})
export class ReExecuteWithLimitedDataDialogComponent {
  dialogRef =
    inject<MatDialogRef<ReExecuteWithLimitedDataDialogComponent>>(MatDialogRef);

  settingsForInitialForecastVersionFormGroup: FormGroup<ForecastVersionSettingsForm> =
    new FormGroup<ForecastVersionSettingsForm>({
      includeDataUntil: new FormControl<Date | null>(null, [
        Validators.required,
      ]),
      isBatchProcessing: new FormControl<boolean>(false, { nonNullable: true }),
      frequency: new FormControl(Resolution.Monthly, { nonNullable: true }),
      useEndOfMonth: new FormControl<boolean>(true, { nonNullable: true }),
      lastRelevantActualDate: new FormControl<Date | null>(null),
    });

  protected readonly Resolution = Resolution;

  private readonly formValid = toSignal(
    this.settingsForInitialForecastVersionFormGroup.statusChanges.pipe(
      map((s) => s === 'VALID'),
    ),
    { initialValue: this.settingsForInitialForecastVersionFormGroup.valid },
  );

  protected readonly buttonConfigs = computed(() => ({
    cancelConfig: {
      dataTestid: 'limited-data-cancel-button',
      handleClick: () => this.onClick(false),
    },
    submitConfig: {
      text: 'RESULT_OVERVIEW.RE_EXECUTE.DIALOG.EXECUTE',
      disabled: !this.formValid(),
      dataTestid: 'limited-data-execute-button',
      handleClick: () => this.onClick(true),
    },
  }));

  onClick(execute: boolean): void {
    if (execute) {
      if (
        this.settingsForInitialForecastVersionFormGroup.valid &&
        this.settingsForInitialForecastVersionFormGroup.value.includeDataUntil
      ) {
        const includeDataUntil = format(
          this.settingsForInitialForecastVersionFormGroup.value
            .includeDataUntil,
          'yyyy-MM-dd',
        );
        const executeConfigurationDto: ExecuteConfigurationDto = {
          includeDataUntil,
        };
        if (
          this.settingsForInitialForecastVersionFormGroup.controls
            .isBatchProcessing?.value &&
          this.settingsForInitialForecastVersionFormGroup.controls.frequency
            ?.value &&
          this.settingsForInitialForecastVersionFormGroup.controls
            .lastRelevantActualDate?.value
        ) {
          executeConfigurationDto.batchRecalculationSettings = {
            frequency:
              this.settingsForInitialForecastVersionFormGroup.controls.frequency
                ?.value,
            useEndOfMonth:
              this.settingsForInitialForecastVersionFormGroup.controls
                .useEndOfMonth?.value ?? false,
            lastRelevantActualDate: format(
              this.settingsForInitialForecastVersionFormGroup.controls
                .lastRelevantActualDate?.value,
              'yyyy-MM-dd',
            ),
          };
        }
        this.dialogRef.close(executeConfigurationDto);
      }
    } else {
      this.dialogRef.close(false);
    }
  }
}
